import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import {LabAutomationFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isConnectFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {getStore} from 'redux-stores/Store';
import * as SchemaConfigReduxActions from 'schemas/spec/SchemaConfigReduxActions';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import InputWrapper from 'ui-library/inputs/InputWrapper';

import AdapterSelector from './AdapterSelector';
import {schemaMetadataSectionStyles} from './SchemaMetadataSection';

interface RunSchemaMetadataSectionProps {
  adapterId: string | null;
  allowInsertRunFromInbox: boolean;
  allowCreateRunFromNotebook: boolean;
  shouldRecordEquipment: boolean;
  isCreating: boolean;
  onUpdate: () => void;
  adapterIdDisabled?: boolean;
  adapterIdDisabledTooltip?: string | null;
}

function RunSchemaMetadataSection(props: RunSchemaMetadataSectionProps): JSX.Element {
  const inputs: Array<JSX.Element> = [];
  if (
    isConnectFeatureEnabled({feature: LabAutomationFeature.LAB_AUTOMATION_ADMIN}) ||
    props.adapterId != null
  ) {
    inputs.push(
      <InputWrapper id={_.uniqueId('adapter')} label="Connection Type" labelStyle={FieldLabel.STYLES.LABEL}>
        <AdapterSelector
          className={css(styles.adapterDropdown)}
          value={props.adapterId}
          onChange={(adapterId: string | null) => {
            getStore().dispatch(SchemaConfigReduxActions.setAdapterId(adapterId));
            props.onUpdate();
          }}
          disabled={props.adapterIdDisabled}
          disabledTooltip={props.adapterIdDisabledTooltip}
        />
      </InputWrapper>
    );
  }
  inputs.push(
    <div>
      <div className="fieldInput-label-container">
        <FieldLabel label="Notebook preferences" labelStyle={FieldLabel.STYLES.LABEL} />
      </div>
      <div className={css(styles.notebookPreferences)}>
        <Tooltip
          text="Cannot disable both notebook options"
          isDisabled={props.allowInsertRunFromInbox}
          placement={FloatingPlacement.LEFT}
        >
          <CheckboxInput
            id="allowCreateRunFromNotebook"
            checked={props.allowCreateRunFromNotebook}
            disabled={!props.allowInsertRunFromInbox}
            label="Allow creating new runs from the notebook"
            onChange={(e) => {
              const allowCreateRunFromNotebook = e.target.checked;
              getStore().dispatch(
                SchemaConfigReduxActions.setSchemaSpecAllowCreateRunFromNLAD(allowCreateRunFromNotebook)
              );
              props.onUpdate();
            }}
          />
        </Tooltip>
        <Tooltip
          text="Cannot disable both notebook options"
          isDisabled={props.allowCreateRunFromNotebook}
          placement={FloatingPlacement.LEFT}
        >
          <CheckboxInput
            id="allowInsertRunFromInbox"
            checked={props.allowInsertRunFromInbox}
            disabled={!props.allowCreateRunFromNotebook}
            dataTestAttrs={{
              component: 'AssaySchemaMetadataSettings',
              element: 'allowInsertRunFromInboxCheckbox',
            }}
            label="Allow inserting runs from inbox in the notebook"
            onChange={(e) => {
              const allowInsertRunFromInbox = e.target.checked;
              getStore().dispatch(
                SchemaConfigReduxActions.setSchemaSpecAllowInsertRunFromInbox(allowInsertRunFromInbox)
              );
              props.onUpdate();
            }}
          />
        </Tooltip>
      </div>
    </div>
  );
  if (BenchlingConfig.ENABLE_EQUIPMENT) {
    inputs.push(
      <div>
        <div className="fieldInput-label-container">
          <FieldLabel label="Additional preferences" labelStyle={FieldLabel.STYLES.LABEL} />
        </div>
        <div className={css(styles.notebookPreferences)}>
          <Tooltip
            text="Cannot edit Record Equipment after schema creation"
            isDisabled={props.isCreating}
            placement={FloatingPlacement.LEFT}
          >
            <CheckboxInput
              id="shouldRecordEquipment"
              checked={props.shouldRecordEquipment}
              disabled={!props.isCreating}
              label="Record Equipment"
              helpText="Associate Benchling equipment with the run"
              onChange={(e) => {
                const shouldRecordEquipment = e.target.checked;
                getStore().dispatch(
                  SchemaConfigReduxActions.setSchemaSpecShouldRecordEquipment(shouldRecordEquipment)
                );
                props.onUpdate();
              }}
            />
          </Tooltip>
        </div>
      </div>
    );
  }
  return (
    <div className={css(schemaMetadataSectionStyles.schemaMetadataSection)}>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>{inputs[0]}</div>
      </div>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>{inputs[1]}</div>
      </div>
      <div className={css(schemaMetadataSectionStyles.schemaMetadataColumn)}>
        <div className={css(schemaMetadataSectionStyles.schemaMetadataField)}>{inputs[2]}</div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  adapterDropdown: {
    flex: 1,
  },
  notebookPreferences: {
    display: 'flex',
    flexDirection: 'column',
  },
});

export default RunSchemaMetadataSection;
